import { createSelector } from '@reduxjs/toolkit';
import { agenciesAdapterToShortData, citiesListFromAppSettings, documentsTypeListFromAppSettings } from 'src/adapters';
import { DEFAULT_CURRENCY } from 'src/consts';
import { RootState } from 'src/store';
import { AppSettings } from 'src/types';

export const appConfigStateSelector = (state: RootState) => state.appConfigs;
export const appSettingsSelector = (state: RootState) => state.appConfigs.config;

export const getCitiesListSelector = createSelector(appSettingsSelector, (config: AppSettings | null) => {
  if (!config) return [];
  if (!config.cities) return [];
  return citiesListFromAppSettings(config);
});

export const colisTypesSelector = createSelector(appSettingsSelector, (config: AppSettings | null) => {
  if (!config) return null;
  return config.colisCatalog;
});

export const listPriceCatalogSelector = createSelector(appSettingsSelector, (config: AppSettings | null) => {
  if (!config) return [];
  if (!config.organization) return [];
  if (!config.organization.settings) return [];
  if (!config.organization.settings.pricesCatalog) return [];
  return config.organization.settings.pricesCatalog;
});

export const documentsTypeSelector = createSelector(appSettingsSelector, (config: AppSettings | null) => {
  if (!config) return [];
  if (!config.documentTypes) return [];
  return documentsTypeListFromAppSettings(config);
});

export const allAgenciesSelector = createSelector(appSettingsSelector, (config: AppSettings | null) => {
  if (!config) return [];
  if (!config.agencies) return [];
  if (!config.agencies.allAgencies) return [];
  return config.agencies.allAgencies;
});

export const allWarehousesSelector = createSelector(appSettingsSelector, (config: AppSettings | null) => {
  if (!config) return [];
  if (!config.warehouses) return [];
  return config.warehouses;
});

export const scopeAgenciesSelector = createSelector(appSettingsSelector, (config: AppSettings | null) => {
  if (!config) return [];
  if (!config.agencies) return [];
  if (!config.agencies.scopeAgencies) return [];
  return config.agencies.scopeAgencies;
});

export const allAgenciesShortDataSelector = createSelector(allAgenciesSelector, (agencies) => {
  return agenciesAdapterToShortData(agencies);
});

export const allWarehousesShortDataSelector = createSelector(allWarehousesSelector, (warehouses) => {
  return agenciesAdapterToShortData(warehouses);
});

export const scopeAgenciesShortDataSelector = createSelector(scopeAgenciesSelector, (agencies) => {
  return agenciesAdapterToShortData(agencies);
});

export const organizationSettingsSelector = createSelector(appSettingsSelector, (config: AppSettings | null) => {
  if (!config) {
    return null;
  }
  if (!config.organization) return null;
  if (!config.organization.settings) return null;

  return config.organization.settings;
});

export const maxDeclaredValueOrganizationSettingsSelector = createSelector(
  appSettingsSelector,
  (config: AppSettings | null) => {
    if (!config) {
      return 0;
    }
    if (!config.organization) return 0;
    if (!config.organization.settings) return 0;

    if (!config.organization.settings.organizationSettings) return 0;
    return config.organization.settings.organizationSettings.maxDeclaredValue;
  },
);

export const organizationCurrencySelector = createSelector(appSettingsSelector, (config: AppSettings | null) => {
  return config?.organization?.settings?.organizationSettings?.currency ?? DEFAULT_CURRENCY;
});

export const organizationConversionRateSelector = createSelector(appSettingsSelector, (config: AppSettings | null) => {
  return {
    MAD_TO_EUR: config?.organization?.settings?.MAD_TO_EUR ?? 0.099,
    EUR_TO_MAD: config?.organization?.settings?.EUR_TO_MAD ?? 11,
  };
});
